import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { esitoUpload } from "@/lib/upload";
import { CheckCircle2, Loader2, MessageCircle, Mail } from "lucide-react";

const CAUSALI_FALLBACK = ["In attesa dal cliente", "In richiesta all'ente", "Smarrito / da rifare", "Non applicabile"];

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();

// Un documento è "presente" se un file su Drive contiene tutte le parole del nome richiesto
const trovaFile = (files, nome) => {
  const parole = norm(nome).split(" ").filter((w) => w.length > 2);
  if (!parole.length) return null;
  return (files || []).find((f) => {
    const n = norm(f.name);
    return parole.every((w) => n.includes(w));
  }) || null;
};

export default function ChecklistDocumenti({ pratica, onUploaded, senzaCornice }) {
  const [tutti, setTutti] = useState(false);
  const [sollecito, setSollecito] = useState(false);
  const richiesti = pratica?.documenti_richiesti || [];
  const clienteId = pratica?.cliente_id;
  const inputRef = useRef(null);
  const [target, setTarget] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const { data, refetch } = useQuery({
    queryKey: ["drive-files", clienteId],
    queryFn: async () => (await api.get(`/clienti/${clienteId}/drive-files`)).data,
    enabled: !!clienteId && richiesti.length > 0,
  });
  const files = data?.files || [];
  const stato = pratica?.documenti_stato || [];
  const causali = pratica?.causali_documento || CAUSALI_FALLBACK;
  const segnati = pratica?.documenti_mancanti || [];
  const statoDi = (d) => stato.find((x) => x.key === d.key) || null;
  const segnatoDi = (d) => segnati.find((x) => x.key === d.key) || null;

  const salvaMancanti = async (voci) => {
    try {
      await api.put(`/pratiche/${pratica.id}/documenti-mancanti`, { documenti: voci });
      onUploaded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Segnalazione non salvata"); }
  };

  const toggleMancante = (d, on) => {
    const altri = segnati.filter((x) => x.key !== d.key);
    salvaMancanti(on ? [...altri, { key: d.key, nome: d.nome, motivo: causali[0], note: "" }] : altri);
  };

  const cambiaCausale = (d, campo, valore) => {
    salvaMancanti(segnati.map((x) => x.key === d.key ? { ...x, [campo]: valore } : x));
  };

  if (!richiesti.length) return null;

  const pick = (doc) => { setTarget(doc); setTimeout(() => inputRef.current?.click(), 0); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    const doc = target;
    setTarget(null);
    if (!file || !doc) return;
    if (doc.max_mb && file.size > doc.max_mb * 1024 * 1024) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      return toast.error(`"${file.name}" pesa ${mb} MB: il limite per "${doc.nome}" è ${doc.max_mb} MB. Comprimi il file e riprova.`);
    }
    setBusyKey(doc.key);
    try {
      const cl = pratica.cliente || {};
      const suffix = [cl.cognome, cl.nome].filter(Boolean).join(" ");
      const fd = new FormData();
      fd.append("files", file);
      fd.append("names", suffix ? `${doc.nome} (${suffix})` : doc.nome);
      fd.append("categoria", "Documenti Cliente");
      const { data: res } = await api.post(`/clienti/${clienteId}/upload`, fd);
      toast.success(esitoUpload(res));
      await refetch();
      onUploaded?.();
    } catch (err) {
      toast.warning(err?.response?.data?.detail || "Upload non riuscito");
    } finally { setBusyKey(null); }
  };

  const presente = (d) => {
    const st = statoDi(d);
    if (st) return st.presente ? { name: st.file, fonte: st.fonte } : null;
    const f = trovaFile(files, d.nome);
    return f ? { name: f.name, fonte: "drive" } : null;
  };
  const mancantiDocs = richiesti.filter((d) => !presente(d));
  const mancanti = mancantiDocs.filter((d) => d.obbligatorio).length;
  const cl = pratica?.cliente || {};

  const testoPromemoria = () => {
    const elenco = mancantiDocs
      .map((d) => `• ${d.nome}${d.obbligatorio ? "" : " (facoltativo)"}${d.max_mb ? ` — max ${d.max_mb} MB` : ""}`)
      .join("\n");
    return `Buongiorno ${cl.nome || ""},\n\nper procedere con la sua pratica${pratica?.tipo_pratica_nome ? ` di ${pratica.tipo_pratica_nome}` : ""} ci servono ancora questi documenti:\n\n${elenco}\n\nPuò inviarli in risposta a questo messaggio. La ringraziamo.\n\nRB Studio`;
  };

  const logDiario = async (canale) => {
    try {
      await api.post(`/pratiche/${pratica.id}/note`, {
        testo: `Promemoria documenti mancanti inviato via ${canale}: ${mancantiDocs.map((d) => d.nome).join(", ")}`,
      });
      onUploaded?.();
    } catch { /* il promemoria è comunque partito */ }
  };

  const promemoriaWhatsapp = async () => {
    let num = String(cl.telefono || "").replace(/\D/g, "");
    if (!num) return toast.error("Il cliente non ha un numero di telefono in anagrafica");
    if (num.length === 10) num = "39" + num;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(testoPromemoria())}`, "_blank");
    await logDiario("WhatsApp");
  };

  const promemoriaEmail = async () => {
    if (!cl.email) return toast.error("Il cliente non ha un'email in anagrafica");
    setBusyKey("__mail");
    try {
      const fd = new FormData();
      fd.append("to", cl.email);
      fd.append("oggetto", `Documenti mancanti — pratica ${pratica?.tipo_pratica_nome || ""}`.trim());
      fd.append("testo", testoPromemoria());
      const { data: res } = await api.post(`/pratiche/${pratica.id}/email-draft`, fd);
      toast.success("Bozza email creata in Gmail (da rivedere e inviare manualmente).");
      if (res.draft_link) window.open(res.draft_link, "_blank");
      await logDiario("bozza email");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione bozza");
    } finally { setBusyKey(null); }
  };

  return (
    <div className={senzaCornice ? "" : "sub"} data-testid="checklist-documenti">
      <div className="subt">
        <span>Checklist documentale</span>
        <span className="flex items-center gap-2.5 normal-case tracking-normal font-normal">
          <span data-testid="checklist-status"
            className={`text-[13px] font-bold ${mancanti ? "text-[#9A3B13]" : "text-[#2E5E3A]"}`}>
            {mancanti ? `${mancanti} mancanti su ${richiesti.length}` : "documentazione completa"}
          </span>
          {mancantiDocs.length > 0 && (
            <button data-testid="checklist-sollecita" onClick={() => setSollecito(!sollecito)}
              className="btn btn-o btn-piccolo">Sollecita al cliente</button>
          )}
        </span>
      </div>

      {sollecito && mancantiDocs.length > 0 && (
        <div className="riga flex-wrap gap-2.5" data-testid="checklist-promemoria">
          <span className="text-[13px] text-[#4A4A48] flex-[999_1_220px]">
            Sollecita i {mancantiDocs.length} documenti mancanti: {mancantiDocs.map((d) => d.nome).join(", ")}
          </span>
          <button data-testid="checklist-wa-btn" onClick={promemoriaWhatsapp} className="btn btn-o btn-piccolo">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </button>
          <button data-testid="checklist-mail-btn" onClick={promemoriaEmail} disabled={busyKey === "__mail"}
            className="btn btn-o btn-piccolo">
            {busyKey === "__mail" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Bozza email
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-2 md:gap-x-0">
        {(tutti ? richiesti : richiesti.slice(0, 6)).map((d) => {
          const f = presente(d);
          const seg = segnatoDi(d);
          const st = statoDi(d);
          return (
            <div key={d.key} data-testid={`checklist-row-${d.key}`}
              className="riga group gap-3 items-start md:odd:border-r md:odd:border-[#F0EEE8]"
              style={!f && d.obbligatorio ? { background: "#FDF6F1" } : undefined}>
              {f ? (
                <CheckCircle2 data-testid={`checklist-ok-${d.key}`} className="w-4 h-4 text-[#2E5E3A] shrink-0 mt-0.5" />
              ) : (
                <span data-testid={`checklist-missing-${d.key}`}
                  className={`shrink-0 w-4 text-center font-bold ${d.obbligatorio ? "text-[#9A3B13]" : "text-[#A8863F]"}`}>!</span>
              )}
              <span className="min-w-0 flex-grow">
                <span className="block truncate" title={d.nome}>{d.nome}</span>
                <span data-testid={`checklist-stato-${d.key}`}
                  className="hidden sm:block text-[12px] text-[#5A5A5A] opacity-0 group-hover:opacity-100 transition-opacity truncate">
                  {f ? `${f.name}${f.fonte === "drive" ? " · Drive" : ""}`
                    : d.obbligatorio ? "obbligatorio" : "facoltativo"}
                  {d.max_mb ? ` · max ${d.max_mb} MB` : ""}
                </span>
                {!f && seg && (
                  <span className="hidden sm:group-hover:flex flex-wrap items-center gap-2 mt-1">
                    <select data-testid={`checklist-causale-${d.key}`} value={seg.motivo || causali[0]}
                      onChange={(e) => cambiaCausale(d, "motivo", e.target.value)}
                      className="text-[12px] px-2 py-1 border border-[#C6C3BA] rounded-[4px] bg-white">
                      {causali.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input data-testid={`checklist-note-${d.key}`} defaultValue={seg.note || ""} placeholder="note"
                      onBlur={(e) => e.target.value !== (seg.note || "") && cambiaCausale(d, "note", e.target.value)}
                      className="flex-1 min-w-[120px] text-[12px] px-2 py-1 border border-[#C6C3BA] rounded-[4px]" />
                  </span>
                )}
              </span>
              <span className="shrink-0 flex items-center gap-2.5">
                {f && (
                  <>
                    <span className="text-[13px] text-[#5A5A5A]">caricato</span>
                    {f.webViewLink && (
                      <a href={f.webViewLink} target="_blank" rel="noreferrer" data-testid={`checklist-open-${d.key}`}
                        className="text-[13px] font-semibold no-underline">Apri</a>
                    )}
                  </>
                )}
                {!f && seg && (
                  <span data-testid={`checklist-attesa-${d.key}`} className="text-[13px] text-[#8A6E2F] text-right">
                    {(seg.motivo || "in attesa").toLowerCase()}
                    {st?.da_giorni ? ` · ${st.da_giorni} giorni` : ""}
                  </span>
                )}
                {!f && !seg && (
                  <button data-testid={`checklist-upload-${d.key}`} onClick={() => pick(d)} disabled={busyKey === d.key}
                    title={d.max_mb ? `Carica (max ${d.max_mb} MB)` : "Carica"} className="btn btn-o btn-mini">
                    {busyKey === d.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Carica"}
                  </button>
                )}
                {!f && (
                  <label className="hidden sm:inline-flex items-center gap-1 text-[12px] text-[#8A6E2F] cursor-pointer
                                    opacity-0 group-hover:opacity-100 transition-opacity">
                    <input type="checkbox" data-testid={`checklist-mancante-${d.key}`} checked={!!seg}
                      onChange={(e) => toggleMancante(d, e.target.checked)} className="accent-[#A8863F]" />
                    attesa
                  </label>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {richiesti.length > 6 && (
        <div className="riga">
          <button data-testid="checklist-vedi-tutti" onClick={() => setTutti(!tutti)}
            className="text-[13px] font-semibold text-[#8A6E2F] hover:text-[#6B5523]">
            {tutti ? "Mostra solo i primi sei" : `Vedi tutti i ${richiesti.length} documenti`}
          </button>
        </div>
      )}

      <div className="nota-tenue">
        I documenti mancanti restano in evidenza ma <strong className="font-bold">non bloccano la pratica</strong>:
        si può generare, incassare e proseguire.
      </div>

      <input ref={inputRef} data-testid="checklist-file-input" type="file" className="hidden"
        accept="image/*,application/pdf,.doc,.docx" onChange={onFile} />
    </div>
  );
}
